import React, { useEffect, useMemo, useState } from 'react';
import { Tabs, Form, Input, Button, Card, Table, Space, message, Upload, Typography, Divider, Modal, Switch } from 'antd';
import { UploadOutlined, PlayCircleOutlined, DeleteOutlined, PlusOutlined, SaveOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';
import 'antd/dist/reset.css';

const { TextArea } = Input;
const { Title, Text } = Typography;

const Coupons = () => {
  const { getAuthHeaders } = useAuth();
  const headers = useMemo(() => ({ 'Content-Type': 'application/json', ...getAuthHeaders() }), [getAuthHeaders]);

  const [settings, setSettings] = useState({ system_prompt: '', openai: { api_key: '', base_url: '', completions_model: 'gpt-4.1' } });
  const [loadingSettings, setLoadingSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);

  const [genForm] = Form.useForm();
  const [generating, setGenerating] = useState(false);
  const [rules, setRules] = useState([]);
  const [loadingRules, setLoadingRules] = useState(false);

  const [execJsonText, setExecJsonText] = useState('{\n  "cart": { "total": 120 },\n  "customer": { "segment": "GOLD" }\n}');
  const [executing, setExecuting] = useState(false);
  const [execResult, setExecResult] = useState(null);
  const [execAsModal, setExecAsModal] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [editCouponCode, setEditCouponCode] = useState('');
  const [editUserPrompt, setEditUserPrompt] = useState('');
  const [editRuleJsonText, setEditRuleJsonText] = useState('');
  const [editStatus, setEditStatus] = useState(true);
  const [editJustJson, setEditJustJson] = useState(false);

  const loadSettings = async () => {
    setLoadingSettings(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/coupons/settings`, { headers: getAuthHeaders() });
      const j = await r.json();
      setSettings(prev => ({ ...prev, ...(j?.config || {}) }));
    } catch (e) {
      message.error('Failed to load settings');
    } finally {
      setLoadingSettings(false);
    }
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/coupons/settings`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(settings),
      });
      if (r.ok) {
        message.success('Settings saved');
      } else {
        const t = await r.text();
        message.error(`Save failed: ${t}`);
      }
    } catch (e) {
      message.error('Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const loadRules = async () => {
    setLoadingRules(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/coupons/rules`, { headers: getAuthHeaders() });
      const j = await r.json();
      setRules(j?.rules || []);
    } catch (e) {
      message.error('Failed to load rules');
    } finally {
      setLoadingRules(false);
    }
  };

  const deleteRule = async (id) => {
    Modal.confirm({
      title: 'Delete rule?',
      content: 'This will remove the coupon rule.',
      onOk: async () => {
        try {
          const r = await fetch(`${API_BASE_URL}/api/v1/coupons/rules/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
          if (r.ok) {
            message.success('Deleted');
            loadRules();
          } else {
            const t = await r.text();
            message.error(`Delete failed: ${t}`);
          }
        } catch (e) {
          message.error('Delete failed');
        }
      }
    });
  };

  const openEdit = (rec) => {
    setEditRecord(rec);
    setEditCouponCode(rec.coupon_code || '');
    setEditUserPrompt(rec.user_prompt || '');
    try {
      setEditRuleJsonText(JSON.stringify(rec.rule_json ?? {}, null, 2));
    } catch {
      setEditRuleJsonText('{}');
    }
    setEditStatus((rec.status || 'active') === 'active');
    setEditJustJson(false);
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editRecord) return;
    setEditSaving(true);
    try {
      let parsed = null;
      try {
        parsed = JSON.parse(editRuleJsonText || '{}');
      } catch (e) {
        message.error('Rule JSON is invalid');
        setEditSaving(false);
        return;
      }
      const payload = {
        coupon_code: editCouponCode,
        user_prompt: editUserPrompt,
        rule_json: parsed,
        status: editStatus ? 'active' : 'inactive',
        rebuild: editJustJson ? false : true
      };
      const r = await fetch(`${API_BASE_URL}/api/v1/coupons/rules/${editRecord.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(payload)
      });
      const t = await r.text();
      if (!r.ok) throw new Error(t);
      message.success('Updated');
      setEditOpen(false);
      setEditRecord(null);
      loadRules();
    } catch (e) {
      message.error(e.message || 'Update failed');
    } finally {
      setEditSaving(false);
    }
  };

  const onGenerate = async (values) => {
    setGenerating(true);
    try {
      const r = await fetch(`${API_BASE_URL}/api/v1/coupons/rules/generate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(values),
      });
      const t = await r.text();
      if (!r.ok) throw new Error(t);
      message.success('Rule generated and saved');
      genForm.resetFields();
      loadRules();
    } catch (e) {
      message.error(`Generate failed: ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const onExecute = async () => {
    setExecuting(true);
    try {
      let obj = null;
      try {
        obj = JSON.parse(execJsonText || '{}');
      } catch (e) {
        message.error('Invalid JSON');
        setExecuting(false);
        return;
      }
      const r = await fetch(`${API_BASE_URL}/api/v1/coupons/execute`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ input: obj }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || 'Execute failed');
      setExecResult(j);
      setExecAsModal(true);
    } catch (e) {
      message.error(e.message || 'Execute failed');
    } finally {
      setExecuting(false);
    }
  };

  useEffect(() => {
    loadSettings();
    loadRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rulesColumns = [
    { title: 'Coupon Code', dataIndex: 'coupon_code', key: 'coupon_code' },
    { title: 'Updated', dataIndex: 'updated_at', key: 'updated_at', width: 220 },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, rec) => (
        <Space>
          <Button type="default" size="small" onClick={() => openEdit(rec)}>Edit</Button>
          <Button danger icon={<DeleteOutlined />} size="small" onClick={() => deleteRule(rec.id)}>Delete</Button>
        </Space>
      )
    },
  ];

  return (
    <div className="container-fluid" style={{ maxWidth: '100%' }}>
      <Title level={3} style={{ marginBottom: 16 }}>Coupons</Title>
      <Tabs
        defaultActiveKey="coupons"
        items={[
          {
            key: 'coupons',
            label: 'Coupons',
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                <Card title="Generate Coupon Rule" extra={<Text type="secondary">Ant Design UI</Text>}>
                  <Form
                    form={genForm}
                    layout="vertical"
                    onFinish={onGenerate}
                    initialValues={{ coupon_code: '', user_prompt: '' }}
                  >
                    <Form.Item label="Coupon Code" name="coupon_code" rules={[{ required: true, message: 'Coupon code is required' }]}>
                      <Input placeholder="e.g., SAVE10" />
                    </Form.Item>
                    <Form.Item
                      label="Rule (natural language user prompt)"
                      name="user_prompt"
                      rules={[{ required: true, message: 'User prompt is required' }]}
                    >
                      <TextArea rows={5} placeholder="e.g., Give 10% off when cart total is at least $100 and customer is in GOLD segment" />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Button type="primary" icon={<PlusOutlined />} htmlType="submit" loading={generating}>
                          Generate & Save
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                </Card>

                <Card title="Existing Rules">
                  <Table
                    rowKey="id"
                    columns={rulesColumns}
                    dataSource={rules}
                    loading={loadingRules}
                    pagination={{ pageSize: 10 }}
                    expandable={{
                      expandedRowRender: (rec) => (
                        <div>
                          <Divider orientation="left">User Prompt</Divider>
                          <pre style={{ whiteSpace: 'pre-wrap' }}>{rec.user_prompt}</pre>
                          <Divider orientation="left">Generated Rule (JSON)</Divider>
                          <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(rec.rule_json, null, 2)}</pre>
                        </div>
                      )
                    }}
                  />
                </Card>

                <Card title="Execute Rules Against JSON">
                  <Text type="secondary">Paste input JSON. The system will check all active rules and show which coupons match.</Text>
                  <div style={{ marginTop: 12 }} />
                  <TextArea rows={10} value={execJsonText} onChange={e => setExecJsonText(e.target.value)} />
                  <div style={{ marginTop: 12 }}>
                    <Space>
                      <Button type="primary" icon={<PlayCircleOutlined />} onClick={onExecute} loading={executing}>
                        Execute
                      </Button>
                    </Space>
                  </div>
                </Card>
              </Space>
            )
          },
          {
            key: 'settings',
            label: 'Settings',
            children: (
              <Card title="Coupons Settings">
                <Form layout="vertical">
                  <Form.Item label="System Prompt for Rule Generation">
                    <TextArea
                      rows={6}
                      value={settings.system_prompt}
                      onChange={e => setSettings(prev => ({ ...prev, system_prompt: e.target.value }))}
                      placeholder="Guidance for the LLM to convert natural language into normalized JSON rules"
                    />
                  </Form.Item>
                  <Divider />
                  <Title level={5}>OpenAI Overrides (optional)</Title>
                  <Form.Item label="Base URL">
                    <Input value={settings.openai?.base_url} onChange={e => setSettings(prev => ({ ...prev, openai: { ...prev.openai, base_url: e.target.value } }))} />
                  </Form.Item>
                  <Form.Item label="API Key">
                    <Input.Password value={settings.openai?.api_key} onChange={e => setSettings(prev => ({ ...prev, openai: { ...prev.openai, api_key: e.target.value } }))} />
                  </Form.Item>
                  <Form.Item label="Completions Model">
                    <Input value={settings.openai?.completions_model} onChange={e => setSettings(prev => ({ ...prev, openai: { ...prev.openai, completions_model: e.target.value } }))} />
                  </Form.Item>
                  <Form.Item>
                    <Space>
                      <Button type="primary" icon={<SaveOutlined />} onClick={saveSettings} loading={savingSettings}>
                        Save Settings
                      </Button>
                    </Space>
                  </Form.Item>
                </Form>
              </Card>
            )
          }
        ]}
      />

      <Modal
        open={execAsModal}
        onCancel={() => setExecAsModal(false)}
        footer={<Button onClick={() => setExecAsModal(false)}>Close</Button>}
        title="Execution Result"
        width={720}
      >
        {execResult ? (
          <div>
            <p><b>Matched Count:</b> {execResult.count}</p>
            <pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(execResult.matched || [], null, 2)}</pre>
          </div>
        ) : <Text type="secondary">No results</Text>}
      </Modal>

      <Modal
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        title="Edit Coupon"
        width={800}
        footer={
          <Space>
            <Button onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button type="primary" loading={editSaving} onClick={saveEdit}>Save</Button>
          </Space>
        }
      >
        <Form layout="vertical">
          <Form.Item label="Coupon Code">
            <Input value={editCouponCode} onChange={e => setEditCouponCode(e.target.value)} />
          </Form.Item>
          <Form.Item label="User Prompt">
            <TextArea rows={4} value={editUserPrompt} onChange={e => setEditUserPrompt(e.target.value)} />
          </Form.Item>
          <Form.Item label="Rule JSON">
            <TextArea rows={10} value={editRuleJsonText} onChange={e => setEditRuleJsonText(e.target.value)} />
          </Form.Item>
          <Form.Item label="Active">
            <Switch checked={editStatus} onChange={setEditStatus} />
          </Form.Item>
          <Form.Item label="Save Mode">
            <Space direction="vertical">
              <Switch checked={editJustJson} onChange={setEditJustJson} />
              <Text type="secondary">
                {editJustJson ? 'Will save only the JSON without regenerating.' : 'Will regenerate from the user prompt.'}
              </Text>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Coupons;


